<template>
  <div
    ref="scrollRoot"
    class="infinity-scroll"
    :class="{
      'infinity-scroll--open': hideShadowItem,
    }"
    :style="styles"
  >
    <div
      v-if="!hideShadowItem"
      ref="shadow-item"
    >
      <slot :item="items[0]" />
    </div>
    <ul
      v-show="hideShadowItem"
      ref="list"
    >
      <li
        v-for="(item, inx) in renderedItems"
        ref="listItem"
        :key="inx"
        class="infinity-scroll--item"
      >
        <slot :item="item" />
      </li>
    </ul>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';

export default {
  name: 'InfinityScroll',
  props: {
    items: {
      type: Array,
      required: true,
    },
    iconLeft: {
      type: String,
      default: '',
    },
    listItemHeight: {
      type: Number,
      default: 0,
    },
    listHeight: {
      type: Number,
      default: 0,
    },
    maxListHeight: {
      type: Number,
      default: 0,
    },
    visibleItems: {
      type: [Number, String],
      required: false,
      default: () => 4,
    },
    maxItems: {
      type: Number,
      default: undefined,
    },
    preRender: {
      type: Number,
      default: 2,
    },
    heightOffset: {
      type: Number,
      default: 0,
    },
    rootClass: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      selectedListWidth: 0,
      currentListHeight: this.maxListHeight
        ? Math.min(this.listHeight || 0, this.maxListHeight)
        : this.listHeight || 0,
      currentVisibleItems: Number(this.visibleItems),
      itemHeight: 0,
      scrollPosition: 0,
      itemPositions: [],
      hideShadowItem: !!this.listItemHeight,
      isInitialized: false,
    };
  },
  computed: {
    renderedItems() {
      const items = [...this.displayedItems];
      const firstVisibleItem = this.itemPositions.findIndex((item) => item > this.scrollPosition);
      const firstItem = this.IOH.clamp(firstVisibleItem - this.preRender, 0, 99999);
      const rendered = items.slice(firstItem, firstVisibleItem + this.currentVisibleItems + this.preRender);

      if (this.$refs.list) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.$refs.list.style.paddingTop = `${firstItem * this.itemHeight}px`;
      }

      return rendered;
    },
    maxVisibleItems() {
      return Math.ceil(this.currentListHeight / this.itemHeight);
    },
    itemsContainIcon() {
      return !!this.displayedItems.filter((x) => x.icon).length;
    },
    itemsContainImage() {
      return !!this.displayedItems.filter((x) => x.url).length;
    },
    displayedItems() {
      if (this.maxItems) {
        return this.items.slice(0, this.maxItems);
      }
      return this.items;
    },
    styles() {
      return {
        '--base-select--selected-list-width': `${this.selectedListWidth}px`,
        '--base-select--list-height': `${this.currentListHeight + this.heightOffset}px`,
      };
    },
  },
  watch: {
    itemHeight() {
      this.setListHeight();
    },
    visibleItems() {
      if (this.maxItems) {
        this.currentVisibleItems = Math.min(this.maxVisibleItems, this.maxItems);
      } else {
        this.currentVisibleItems = Number(this.visibleItems);
      }
      this.initialize();
    },
    items() {
      if (this.items.length && !this.isInitialized) {
        this.initialize();
        this.$nextTick(() => {
          this.$refs.scrollRoot.addEventListener('scroll', this.handleScroll);
        });
      } else {
        this.setListHeight();
        this.currentListHeight = this.maxListHeight
          ? Math.min(this.listHeight || this.getListHeight(), this.maxListHeight)
          : this.listHeight || this.getListHeight();
        this.setItemPositions();
        this.$emit('loading', false);
      }
    },
  },
  mounted() {
    this.$emit('loading', true);
    if (this.rootClass) {
      document.querySelector(this.rootClass).appendChild(this.$refs.scrollRoot);
    }

    if (this.items.length) {
      this.initialize();
      this.$refs.scrollRoot.addEventListener('scroll', this.handleScroll);
    }
  },
  beforeDestroy() {
    this.$refs.scrollRoot.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    initialize() {
      this.hideShadowItem = !!this.listItemHeight;
      let itemHeight = this.listItemHeight;
      this.currentListHeight = this.listHeight || this.getListHeight();
      this.setListHeight();

      this.$nextTick(debounce(() => {
        if (!itemHeight) {
          itemHeight = this.$refs['shadow-item'].clientHeight;
          this.hideShadowItem = true;
        }
        if (this.listHeight) {
          const height = this.maxListHeight ? Math.min(this.listHeight, this.maxListHeight) : this.listHeight;
          this.currentVisibleItems = Math.ceil(height / itemHeight);
        }
        this.itemHeight = itemHeight;
        this.currentListHeight = this.listHeight || this.getListHeight();
        this.setItemPositions();
        this.setListHeight();
        this.$emit('loading', false);
        this.isInitialized = true;
      }, 500));
    },
    handleScroll() {
      this.scrollPosition = this.$refs.list.offsetParent.scrollTop;
    },
    setItemPositions() {
      this.itemPositions = this.displayedItems.map((item, index) => (index + 1) * this.itemHeight);
    },
    visibilityItemIcon(item) {
      return {
        visibility: item.divider !== false,
      };
    },
    setListHeight() {
      if (this.$refs.list) {
        this.$refs.list.style.height = `${this.displayedItems.length * this.itemHeight}px`;
      }
    },
    getListHeight() {
      const padding = 0;
      if (this.displayedItems.length > parseInt(this.currentVisibleItems, 10)) {
        let result = 0;
        for (let i = 0; i < parseInt(this.currentVisibleItems, 10); i += 1) {
          result += this.itemHeight + padding;
        }

        return this.maxListHeight ? Math.min(result, this.maxListHeight) : result;
      }
      return this.maxListHeight
        ? Math.min(this.displayedItems.length * (this.itemHeight + padding), this.maxListHeight)
        : this.displayedItems.length * (this.itemHeight + padding);
    },
  },
};
</script>

<style lang="scss" scoped>
@mixin icons {
    min-width: 2.5em;
    font-size: 1.1em;
    padding: 0 0.8em;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-basis: 2em;
}

@mixin divider {
    border-left: 1px solid var(--base-input--accent-color);
    height: var(--base-input--divider-height);
}
.infinity-scroll {
    position: relative;
    height: 0;
    width: 100%;
    overflow: hidden;
    cursor: pointer;
    background-color: #fff;
    border: 0 solid transparent;
    box-shadow: 0 0 0 0 transparent;
    pointer-events: none;

    &--item-icon {
        @include icons;
    }

    &--item-image {
        @include icons;
        padding: 0 1.5rem;
        height: 100%;
        min-width: 5.5em;

        span {
            height: 100%;
            width: 100%;
            background-size: cover;
        }
    }

    &--item-divider {
        @include divider;
    }

    &--item-label {
        display: flex;
        align-items: center;
        flex: 1 0 0;
        padding: 0 1.5em;
    }

    &--open {
        height: var(--base-select--list-height);
        overflow-y: auto;
        overscroll-behavior: contain;
        touch-action: pan-y;
        pointer-events: all;
    }
}
</style>
